var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
export var switchInputIdentifier = 'citrus-switch-input-m725t';
import React, { useCallback, useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { inputChange, useCitrusForm } from '@citrus/form';
import { Input } from '@citrus/input';
import { LabelWrapper as XLabelWrapper, SwitchCompactInput, SwitchError, SwitchInput, SwitchLabel, SwitchWrapper, } from './Switch.styled';
import { getAutomation, getDebug, useCitrusDebug, useFormInputId, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import { isNotNilOrEmpty, only, isString } from '@pcty/utils';
var VERSION = '6.3.0';
var Switch = React.forwardRef(function (_a, ref) {
    var _b;
    var automationId = _a.automationId, className = _a.className, compact = _a.compact, css = _a.css, disabled = _a.disabled, _c = _a.emptyLabel, emptyLabel = _c === void 0 ? false : _c, errorMessage = _a.errorMessage, footnote = _a.footnote, _d = _a.hideOptionalLabel, hideOptionalLabel = _d === void 0 ? false : _d, id = _a.id, label = _a.label, labelAddOn = _a.labelAddOn, _e = _a.toggledOn, toggledOn = _e === void 0 ? false : _e, _f = _a.labelPosition, labelPosition = _f === void 0 ? 'top' : _f, onChange = _a.onChange, textOff = _a.textOff, textOn = _a.textOn, _g = _a.required, required = _g === void 0 ? false : _g, _h = _a.showRequiredLabel, showRequiredLabel = _h === void 0 ? false : _h, showValidationState = _a.showValidationState, tooltip = _a.tooltip, valid = _a.valid, validate = _a.validate, __TYPE = _a.__TYPE, other = __rest(_a, ["automationId", "className", "compact", "css", "disabled", "emptyLabel", "errorMessage", "footnote", "hideOptionalLabel", "id", "label", "labelAddOn", "toggledOn", "labelPosition", "onChange", "textOff", "textOn", "required", "showRequiredLabel", "showValidationState", "tooltip", "valid", "validate", "__TYPE"]);
    var isCitron = useTheme().citrus2022;
    var debug = useCitrusDebug();
    var _j = useCitrusForm(), form = _j.form, dispatch = _j.dispatch, customValidate = _j.validate, formDisabled = _j.disabled;
    var isRequired = isString(required) ? true : required !== null && required !== void 0 ? required : false;
    var requiredMessage = isString(required) ? required : undefined;
    // #region state
    var _k = useState({ valid: false }), _validation = _k[0], setValidation = _k[1];
    var formInputId = useFormInputId(id);
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    // #endregion state
    // #region callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var validateRequired = useCallback(function (value) {
        if (!value) {
            return {
                valid: false,
                errorMessage: requiredMessage !== null && requiredMessage !== void 0 ? requiredMessage : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, requiredMessage]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid, errorMessage: errorMessage };
        var validation = { valid: true };
        if (isRequired) {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, {
                id: id,
                label: label,
                required: isRequired,
            });
        }
        return validation;
    }, [errorMessage, id, isRequired, label, userManagedValidation, valid, validate, validateRequired]);
    var _onChange = useCallback(function (e) {
        var value = e.target.checked;
        var validation = getValidation(value);
        setValidation(only(validation, ['valid', 'errorMessage']));
        onChange === null || onChange === void 0 ? void 0 : onChange({ id: id, isOn: value, validation: validation });
        if (!userManagedValidation) {
            updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: true }));
        }
    }, [formInputId, getValidation, id, label, onChange, updateForm, userManagedValidation]);
    // #endregion callbacks
    // #region effects
    useValidationOnMount(id, label, toggledOn, userManagedValidation, getValidation, setValidation, updateForm);
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    // #endregion effects
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b), className || '');
    var Component = compact || isCitron ? SwitchCompactInput : SwitchInput;
    var wrapperWidth = compact || isCitron ? 50 : 180;
    var LabelWrapper = label ? React.Fragment : XLabelWrapper;
    if (isCitron &&
        !_validation.valid &&
        (showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState)) {
        Component = SwitchError;
    }
    return (React.createElement(Input, __assign({ automationId: automationId, className: switchInputIdentifier, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, ignoreWrappedClick: true, label: label, labelAddOn: labelAddOn, labelPosition: labelPosition, ref: ref, required: isRequired, showRequiredLabel: showRequiredLabel, showValidationState: showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState, tooltip: tooltip, valid: _validation.valid, wrapChildren: true }, other),
        React.createElement(LabelWrapper, null,
            React.createElement(SwitchWrapper, __assign({ className: classes, style: { maxWidth: wrapperWidth } }, getAutomation(automationId), getDebug(debug, __TYPE, VERSION)),
                React.createElement(Component, __assign({}, getAutomation(automationId, 'input'), other, { "aria-checked": toggledOn, checked: toggledOn, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, id: id, onChange: _onChange, required: isRequired, type: "checkbox" })),
                React.createElement(SwitchLabel, { "aria-hidden": "true", "data-off": textOff, "data-on": textOn })))));
});
Switch.defaultProps = {
    __TYPE: 'Switch',
};
export default Switch;
